import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  CalendarBlank, Plus, Trash, PencilSimple, Copy, GoogleLogo, Link as LinkIcon,
  CheckCircle, XCircle, Clock, Sparkle,
} from '@phosphor-icons/react';
import { bookingApi, googleCalendarApi } from '../lib/bookingApi';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DURATIONS = [15, 30, 45, 60, 90, 120];

const EMPTY_EVENT_TYPE = {
  title: '',
  description: '',
  duration_minutes: 30,
  location: '',
  color: '#002677',
  max_per_day: '',
  min_notice_minutes: 60,
  buffer_before_minutes: 0,
  buffer_after_minutes: 0,
  active: true,
};

const COLORS = ['#002677', '#00B140', '#FF671F', '#8347AD', '#E4002B', '#41B6E6'];

export default function BookingSettingsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form state
  const [profileForm, setProfileForm] = useState({ slug: '', timezone: '', welcome: '' });
  const [availability, setAvailability] = useState([]);
  const [etDialog, setEtDialog] = useState({ open: false, editing: null, form: EMPTY_EVENT_TYPE });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await bookingApi.getProfile();
      setData(d);
      setProfileForm({
        slug: d.profile.slug || '',
        timezone: d.profile.timezone || 'Europe/Berlin',
        welcome: d.profile.welcome || '',
      });
      setAvailability(d.availability || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load booking settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle ?google=connected redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      toast.success('Google Calendar connected');
      const url = window.location.pathname;
      window.history.replaceState({}, '', url);
      load();
    }
  }, [load]);

  const saveProfile = async () => {
    try {
      await bookingApi.updateProfile(profileForm);
      toast.success('Profile saved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const saveAvailability = async () => {
    try {
      await bookingApi.updateProfile({ availability });
      toast.success('Availability saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const toggleDay = (wd) => {
    setAvailability((cur) => {
      const exists = cur.find((w) => w.weekday === wd);
      if (exists) return cur.filter((w) => w.weekday !== wd);
      return [...cur, { weekday: wd, start: '09:00', end: '17:00' }]
        .sort((a, b) => a.weekday - b.weekday);
    });
  };

  const updateDayTime = (wd, key, value) => {
    setAvailability((cur) => cur.map((w) => (w.weekday === wd ? { ...w, [key]: value } : w)));
  };

  // ---- Event type CRUD ----
  const openCreateEt = () => setEtDialog({ open: true, editing: null, form: EMPTY_EVENT_TYPE });
  const openEditEt = (et) => setEtDialog({
    open: true,
    editing: et,
    form: { ...EMPTY_EVENT_TYPE, ...et, max_per_day: et.max_per_day || '' },
  });

  const saveEt = async () => {
    const { editing, form } = etDialog;
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    const payload = {
      ...form,
      title: form.title.trim(),
      description: (form.description || '').trim(),
      max_per_day: form.max_per_day === '' ? null : Number(form.max_per_day),
      duration_minutes: Number(form.duration_minutes) || 30,
      min_notice_minutes: Number(form.min_notice_minutes) || 0,
      buffer_before_minutes: Number(form.buffer_before_minutes) || 0,
      buffer_after_minutes: Number(form.buffer_after_minutes) || 0,
    };
    try {
      if (editing) await bookingApi.updateEventType(editing.id, payload);
      else await bookingApi.createEventType(payload);
      toast.success(editing ? 'Event type updated' : 'Event type created');
      setEtDialog({ ...etDialog, open: false });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const deleteEt = async (et) => {
    if (!window.confirm(`Delete "${et.title}"?`)) return;
    try {
      await bookingApi.deleteEventType(et.id);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // ---- Google Calendar ----
  const connectGoogle = async () => {
    try {
      const { data: r } = await googleCalendarApi.start();
      window.location.href = r.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start Google flow');
    }
  };
  const disconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google Calendar? Existing bookings stay, but future ones won\'t sync.')) return;
    try {
      await googleCalendarApi.disconnect();
      toast.success('Google Calendar disconnected');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Disconnect failed');
    }
  };

  if (loading || !data) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }

  const publicUrl = `${window.location.origin}/book/${data.profile.slug}`;

  return (
    <div className="space-y-8" data-testid="booking-settings-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-[#002677]">
          <CalendarBlank size={20} weight="duotone" />
          <span className="text-xs font-semibold uppercase tracking-[0.25em]">Calendly · Free</span>
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">
          My booking page
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure your public booking link, event types, weekly availability and Google Calendar sync.
        </p>
      </div>

      {/* Public URL banner */}
      <Card className="rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-[#002677]/5 border-[#002677]/20">
        <LinkIcon size={20} weight="duotone" className="text-[#002677] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Your public booking page</p>
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-mono text-[#002677] hover:underline break-all"
            data-testid="booking-public-url"
          >
            {publicUrl}
          </a>
        </div>
        <Button
          variant="outline"
          className="rounded-sm"
          onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Link copied'); }}
          data-testid="booking-copy-link"
        >
          <Copy size={14} weight="bold" className="mr-1.5" /> Copy
        </Button>
      </Card>

      {/* Profile + Google Calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-sm p-5">
          <h2 className="font-heading font-semibold text-lg mb-4">Profile</h2>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Slug</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">{window.location.origin}/book/</span>
                <Input
                  data-testid="booking-slug"
                  value={profileForm.slug}
                  onChange={(e) => setProfileForm({ ...profileForm, slug: e.target.value })}
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Timezone (IANA)</Label>
              <Input
                data-testid="booking-tz"
                value={profileForm.timezone}
                placeholder="Europe/Berlin"
                onChange={(e) => setProfileForm({ ...profileForm, timezone: e.target.value })}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Welcome message (shown on public page)</Label>
              <Textarea
                rows={3}
                data-testid="booking-welcome"
                value={profileForm.welcome}
                onChange={(e) => setProfileForm({ ...profileForm, welcome: e.target.value })}
                className="rounded-sm"
                placeholder="e.g. Looking forward to chatting!"
              />
            </div>
            <Button
              onClick={saveProfile}
              className="rounded-sm bg-[#002677] hover:bg-[#002677]/90"
              data-testid="booking-profile-save"
            >
              Save profile
            </Button>
          </div>
        </Card>

        <Card className="rounded-sm p-5">
          <h2 className="font-heading font-semibold text-lg mb-2">Google Calendar</h2>
          <p className="text-sm text-muted-foreground mb-4">
            When connected, new bookings appear in your Google Calendar with a Google Meet link.
            Existing Google Calendar events block slots on your public page automatically.
          </p>
          {data.google_calendar_connected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle size={18} weight="fill" className="text-[#00B140]" />
                <span>Connected as <strong>{data.google_calendar_email || 'your Google account'}</strong></span>
              </div>
              <Button
                variant="outline"
                onClick={disconnectGoogle}
                className="rounded-sm"
                data-testid="booking-gcal-disconnect"
              >
                <XCircle size={14} className="mr-1.5" /> Disconnect
              </Button>
            </div>
          ) : (
            <Button
              onClick={connectGoogle}
              className="rounded-sm bg-white border border-border text-foreground hover:bg-accent/40"
              data-testid="booking-gcal-connect"
            >
              <GoogleLogo size={16} weight="bold" className="mr-2" /> Connect Google Calendar
            </Button>
          )}
        </Card>
      </div>

      {/* Weekly availability */}
      <Card className="rounded-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-semibold text-lg">Weekly availability</h2>
          <Button
            onClick={saveAvailability}
            className="rounded-sm bg-[#002677] hover:bg-[#002677]/90"
            data-testid="booking-availability-save"
          >
            Save availability
          </Button>
        </div>
        <div className="space-y-2">
          {WEEKDAYS.map((d, idx) => {
            const window_ = availability.find((w) => w.weekday === idx);
            return (
              <div key={d} className="flex items-center gap-3 py-2 border-b border-border last:border-b-0">
                <Switch
                  checked={!!window_}
                  onCheckedChange={() => toggleDay(idx)}
                  data-testid={`booking-availability-toggle-${idx}`}
                />
                <span className="w-12 text-sm font-semibold">{d}</span>
                {window_ ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={window_.start}
                      onChange={(e) => updateDayTime(idx, 'start', e.target.value)}
                      className="rounded-sm h-9 w-28"
                      data-testid={`booking-availability-start-${idx}`}
                    />
                    <span className="text-sm text-muted-foreground">–</span>
                    <Input
                      type="time"
                      value={window_.end}
                      onChange={(e) => updateDayTime(idx, 'end', e.target.value)}
                      className="rounded-sm h-9 w-28"
                      data-testid={`booking-availability-end-${idx}`}
                    />
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Unavailable</span>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Event types */}
      <Card className="rounded-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading font-semibold text-lg">Event types</h2>
            <p className="text-sm text-muted-foreground">Each has its own URL and rules (duration, daily cap, buffers).</p>
          </div>
          <Button
            onClick={openCreateEt}
            className="rounded-sm bg-[#002677] hover:bg-[#002677]/90"
            data-testid="booking-event-type-new"
          >
            <Plus size={16} weight="bold" className="mr-1.5" /> New event type
          </Button>
        </div>
        {data.event_types.length === 0 ? (
          <div className="border border-dashed border-border rounded-sm p-8 text-center">
            <Sparkle size={32} weight="duotone" className="mx-auto text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">No event types yet. Create your first to start accepting bookings.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.event_types.map((et) => {
              const url = `${publicUrl}/${et.slug}`;
              return (
                <div
                  key={et.id}
                  className="border border-border rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:shadow-sm transition-shadow"
                  data-testid={`booking-event-type-${et.id}`}
                  style={{ borderLeft: `4px solid ${et.color || '#002677'}` }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{et.title}</span>
                      <Badge variant="secondary" className="rounded-sm h-5">{et.duration_minutes} min</Badge>
                      {!et.active && <Badge variant="outline" className="rounded-sm h-5">Inactive</Badge>}
                      {et.max_per_day && (
                        <Badge variant="outline" className="rounded-sm h-5">Cap {et.max_per_day}/day</Badge>
                      )}
                    </div>
                    {et.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{et.description}</p>
                    )}
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-mono text-[#002677] hover:underline mt-1.5 inline-block break-all"
                    >
                      {url}
                    </a>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm" variant="ghost" className="h-8 rounded-sm"
                      onClick={() => { navigator.clipboard.writeText(url); toast.success('Link copied'); }}
                      title="Copy link"
                    >
                      <Copy size={14} />
                    </Button>
                    <Button
                      size="sm" variant="ghost" className="h-8 rounded-sm"
                      onClick={() => openEditEt(et)}
                      data-testid={`booking-event-type-edit-${et.id}`}
                    >
                      <PencilSimple size={14} />
                    </Button>
                    <Button
                      size="sm" variant="ghost" className="h-8 rounded-sm text-destructive hover:text-destructive"
                      onClick={() => deleteEt(et)}
                      data-testid={`booking-event-type-delete-${et.id}`}
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <ExceptionsPanel publicUrl={publicUrl} />
      <EmbedPanel publicUrl={publicUrl} eventTypes={data.event_types} />

      {/* Event type editor dialog */}      <Dialog open={etDialog.open} onOpenChange={(o) => setEtDialog({ ...etDialog, open: o })}>
        <DialogContent className="rounded-sm sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {etDialog.editing ? 'Edit event type' : 'New event type'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                data-testid="booking-et-title"
                value={etDialog.form.title}
                onChange={(e) => setEtDialog((s) => ({ ...s, form: { ...s.form, title: e.target.value } }))}
                placeholder="15-min intro call"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Description</Label>
              <Textarea
                rows={2}
                value={etDialog.form.description}
                onChange={(e) => setEtDialog((s) => ({ ...s, form: { ...s.form, description: e.target.value } }))}
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Duration</Label>
                <div className="flex flex-wrap gap-1.5">
                  {DURATIONS.map((d) => {
                    const active = Number(etDialog.form.duration_minutes) === d;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setEtDialog((s) => ({ ...s, form: { ...s.form, duration_minutes: d } }))}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          active ? 'bg-[#002677] text-white border-[#002677]' : 'bg-background hover:border-[#002677]/40'
                        }`}
                      >
                        {d < 60 ? `${d} min` : `${d / 60}h${d % 60 ? ` ${d % 60}m` : ''}`}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Color</Label>
                <div className="flex gap-2">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setEtDialog((s) => ({ ...s, form: { ...s.form, color: c } }))}
                      className={`h-7 w-7 rounded-sm border-2 transition-transform ${
                        etDialog.form.color === c ? 'border-foreground scale-110' : 'border-transparent'
                      }`}
                      style={{ background: c }}
                      aria-label={`Color ${c}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                Location / meeting link (shown to customer)
              </Label>
              <Input
                value={etDialog.form.location}
                onChange={(e) => setEtDialog((s) => ({ ...s, form: { ...s.form, location: e.target.value } }))}
                placeholder="Zoom URL, office address, or 'Google Meet (auto-generated)'"
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Max / day</Label>
                <Input
                  type="number" min="0"
                  value={etDialog.form.max_per_day}
                  onChange={(e) => setEtDialog((s) => ({ ...s, form: { ...s.form, max_per_day: e.target.value } }))}
                  placeholder="Unlimited"
                  className="rounded-sm"
                  data-testid="booking-et-cap"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Min notice (min)</Label>
                <Input
                  type="number" min="0"
                  value={etDialog.form.min_notice_minutes}
                  onChange={(e) => setEtDialog((s) => ({ ...s, form: { ...s.form, min_notice_minutes: e.target.value } }))}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Buffer after (min)</Label>
                <Input
                  type="number" min="0"
                  value={etDialog.form.buffer_after_minutes}
                  onChange={(e) => setEtDialog((s) => ({ ...s, form: { ...s.form, buffer_after_minutes: e.target.value } }))}
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-border">
              <Switch
                checked={etDialog.form.active}
                onCheckedChange={(v) => setEtDialog((s) => ({ ...s, form: { ...s.form, active: v } }))}
              />
              <span className="text-sm">Active (visible on public page)</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEtDialog({ ...etDialog, open: false })} className="rounded-sm">Cancel</Button>
            <Button onClick={saveEt} className="rounded-sm bg-[#002677] hover:bg-[#002677]/90"
                    data-testid="booking-et-save">
              {etDialog.editing ? 'Save changes' : 'Create event type'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// =============================================================================
// Date-specific availability exceptions
// =============================================================================
function ExceptionsPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [form, setForm] = React.useState({ date: '', type: 'off', start: '09:00', end: '17:00', note: '' });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await bookingApi.listExceptions();
      setItems(r || []);
    } catch (_) { setItems([]); } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.date) { toast.error('Pick a date'); return; }
    const payload = { date: form.date, type: form.type, note: form.note };
    if (form.type === 'custom') {
      payload.windows = [{ start: form.start, end: form.end }];
    }
    try {
      await bookingApi.upsertException(payload);
      toast.success('Exception saved');
      setForm({ date: '', type: 'off', start: '09:00', end: '17:00', note: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };
  const remove = async (day) => {
    try {
      await bookingApi.deleteException(day);
      toast.success('Removed');
      load();
    } catch (_) { toast.error('Failed'); }
  };

  return (
    <Card className="rounded-sm p-5" data-testid="booking-exceptions">
      <h2 className="font-heading font-semibold text-lg">Date exceptions</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Override your weekly availability for specific dates — mark a day off or use custom hours.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
        <input
          type="date" value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          className="h-10 rounded-sm border border-input bg-background px-3 text-sm"
          data-testid="exception-date"
        />
        <select
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
          className="h-10 rounded-sm border border-input bg-background px-3 text-sm"
          data-testid="exception-type"
        >
          <option value="off">Day off</option>
          <option value="custom">Custom hours</option>
        </select>
        {form.type === 'custom' && (
          <>
            <input type="time" value={form.start}
                   onChange={(e) => setForm({ ...form, start: e.target.value })}
                   className="h-10 rounded-sm border border-input bg-background px-3 text-sm" />
            <input type="time" value={form.end}
                   onChange={(e) => setForm({ ...form, end: e.target.value })}
                   className="h-10 rounded-sm border border-input bg-background px-3 text-sm" />
          </>
        )}
        <Input
          placeholder="Note (optional)" value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="rounded-sm"
        />
      </div>
      <Button onClick={add} className="rounded-sm bg-[#002677] hover:bg-[#002677]/90 mb-4"
              data-testid="exception-add">
        <Plus size={14} weight="bold" className="mr-1.5" /> Add exception
      </Button>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No exceptions yet.</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((it) => (
            <div key={it.date}
                 className="border border-border rounded-sm p-3 flex items-center gap-3 text-sm"
                 data-testid={`exception-row-${it.date}`}>
              <span className="font-mono font-semibold w-28">{it.date}</span>
              {it.type === 'off' ? (
                <Badge variant="outline" className="rounded-sm">Day off</Badge>
              ) : (
                <Badge variant="outline" className="rounded-sm">
                  {it.windows?.[0]?.start || '?'} – {it.windows?.[0]?.end || '?'}
                </Badge>
              )}
              {it.note && <span className="text-muted-foreground text-xs flex-1 truncate">{it.note}</span>}
              <Button size="sm" variant="ghost" className="h-8 rounded-sm text-destructive hover:text-destructive"
                      onClick={() => remove(it.date)}>
                <Trash size={14} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


// =============================================================================
// Embed snippet — inline iframe + popup widget
// =============================================================================
function EmbedPanel({ publicUrl, eventTypes }) {
  const [picked, setPicked] = React.useState(eventTypes[0]?.slug || '');
  React.useEffect(() => {
    if (!picked && eventTypes.length) setPicked(eventTypes[0].slug);
  }, [eventTypes, picked]);

  const embedTarget = picked ? `${publicUrl}/${picked}?embed=1` : `${publicUrl}?embed=1`;

  const inlineSnippet = `<!-- BIL · Schedule & Remind — inline embed -->
<iframe
  src="${embedTarget}"
  width="100%"
  height="720"
  frameborder="0"
  style="border: 1px solid #e5e7eb; border-radius: 4px;"
  title="Book a time"
></iframe>`;

  const popupSnippet = `<!-- BIL · Schedule & Remind — popup widget -->
<a
  href="${embedTarget.replace('?embed=1', '')}"
  onclick="event.preventDefault(); window.open(this.href + '?embed=1', 'bil-book', 'width=900,height=760');"
  style="display:inline-block;padding:10px 18px;background:#002677;color:#fff;text-decoration:none;border-radius:4px;font-family:system-ui,sans-serif;font-weight:600;"
>
  Book a time
</a>`;

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success('Copied to clipboard'); };

  return (
    <Card className="rounded-sm p-5" data-testid="booking-embed-panel">
      <h2 className="font-heading font-semibold text-lg">Embed on your website</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Paste one of these snippets into your site — visitors can book without leaving the page.
      </p>
      {eventTypes.length > 1 && (
        <div className="mb-4">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Event type for the embed</Label>
          <select
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            className="block mt-1.5 h-10 rounded-sm border border-input bg-background px-3 text-sm w-full sm:w-72"
          >
            {eventTypes.map((et) => (
              <option key={et.id} value={et.slug}>{et.title} · {et.duration_minutes} min</option>
            ))}
          </select>
        </div>
      )}
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
              Inline iframe (recommended)
            </Label>
            <Button size="sm" variant="outline" onClick={() => copy(inlineSnippet)} className="rounded-sm h-7"
                    data-testid="embed-copy-inline">
              <Copy size={12} weight="bold" className="mr-1" /> Copy
            </Button>
          </div>
          <pre className="text-xs bg-muted/40 border border-border rounded-sm p-3 overflow-x-auto font-mono">
{inlineSnippet}
          </pre>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
              Popup button
            </Label>
            <Button size="sm" variant="outline" onClick={() => copy(popupSnippet)} className="rounded-sm h-7"
                    data-testid="embed-copy-popup">
              <Copy size={12} weight="bold" className="mr-1" /> Copy
            </Button>
          </div>
          <pre className="text-xs bg-muted/40 border border-border rounded-sm p-3 overflow-x-auto font-mono">
{popupSnippet}
          </pre>
        </div>
      </div>
    </Card>
  );
}
